import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';
import { ColumnChart } from '@/features/dashboard-charts';
import { getExportOptions } from '@/features/chart-export';
import { DashboardFilterBar } from '@/features/dashboard-filters';
import { PageHeader } from '@/shared/ui';

import { useTransportOverview } from '../lib/use-transport-overview';
import { TransportStats, ChartSection, TopListsGrid } from './components';

export function TransportOverviewPage() {
  const { t } = useTranslation();
  const {
    data,
    formattedStats,
    vehicleCategories,
    isLoading,
    isFetching,
    error,
    urlState,
    updateOption,
    handleDateRangeChange,
    projectId,
    siteId
  } = useTransportOverview();

  const exportOptions = useMemo(() => getExportOptions('transport-overview'), []);

  if (error) {
    return (
      <div>
        <Card className="p-4 md:p-6">
          <p className="text-destructive">Error loading transport overview data</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <PageHeader title={t('report.transport.overview.title', 'Transport Overview')} />

      <DashboardFilterBar
        variant="transport"
        options={urlState}
        onOptionChange={updateOption}
        onDateRangeChange={handleDateRangeChange}
        resetKey={`${projectId}-${siteId}`}
        vehicleCategories={vehicleCategories}
      />

      <TransportStats stats={formattedStats} isLoading={isLoading || isFetching} />

      <ChartSection>
        <ColumnChart
          data={data?.transport}
          height={400}
          loading={isLoading || isFetching}
          showExport
          exportOptions={exportOptions}
        />
      </ChartSection>

      <TopListsGrid
        topTransporters={data?.topTransporters}
        topTransportUsers={data?.topTransportUsers}
        isLoading={isLoading || isFetching}
      />
    </div>
  );
}
